import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Eye, EyeOff, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { publicApi } from '../services/api'
import { signIn } from 'aws-amplify/auth'
import { Logo } from '../components/Logo'

type SignUpStep = 'credentials' | 'verification'

interface SignUpData {
  email: string
  password: string
  firstName: string
  lastName: string
  companyName: string
  acceptedTerms: boolean
  userId?: string
}

interface PasswordValidation {
  minLength: boolean
  hasUppercase: boolean
  hasLowercase: boolean
  hasNumber: boolean
}

export function SignUpPage() {
  const { user, loading } = useAuth()
  const [step, setStep] = useState<SignUpStep>('credentials')
  const [signUpData, setSignUpData] = useState<SignUpData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    companyName: '',
    acceptedTerms: false,
    userId: undefined
  })
  const [confirmPassword, setConfirmPassword] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  const validatePassword = (password: string): PasswordValidation => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password)
    }
  }

  const isPasswordValid = (password: string): boolean => {
    const validation = validatePassword(password)
    return validation.minLength && validation.hasUppercase && validation.hasLowercase && validation.hasNumber
  }

  const updateSignUpData = (field: keyof SignUpData, value: string | boolean) => {
    setSignUpData(prev => ({ ...prev, [field]: value }))
  }

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (signUpData.password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    
    if (!isPasswordValid(signUpData.password)) {
      toast.error('Password does not meet requirements')
      return
    }

    if (!signUpData.firstName.trim() || !signUpData.lastName.trim() || !signUpData.companyName.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    
    if (!signUpData.acceptedTerms) {
      toast.error('Please accept the terms of service to continue')
      return
    }
    
    setIsLoading(true)
    
    try {
      const response = await publicApi.signUp(
        signUpData.email,
        signUpData.password,
        signUpData.firstName,
        signUpData.lastName,
        signUpData.companyName
      )
      
      setSignUpData(prev => ({ ...prev, userId: response.userId }))
      toast.success('Verification code sent to your email!')
      setStep('verification')
    } catch (error: any) {
      console.error('Sign up error:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to sign up'
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Please enter a valid verification code')
      return
    }
    
    setIsLoading(true)
    
    try {
      await publicApi.verifySignUp(signUpData.email, verificationCode)
      
      await publicApi.completeSignUp(
        signUpData.userId!,
        signUpData.email,
        signUpData.firstName,
        signUpData.lastName,
        signUpData.companyName,
        ''
      )
      
      toast.success('Email verified! Signing you in...')
      
      await signIn({
        username: signUpData.email,
        password: signUpData.password
      })
      
      await new Promise(resolve => setTimeout(resolve, 500))
      window.location.href = '/dashboard'
    } catch (error: any) {
      console.error('Verification error:', error)
      
      let errorMessage = 'Failed to verify code'
      
      if (error.response?.status === 400) {
        const backendMessage = error.response?.data?.error || error.response?.data?.message
        if (backendMessage?.includes('Invalid verification code') || backendMessage?.includes('CodeMismatchException')) {
          errorMessage = 'Invalid verification code. Please check and try again.'
        } else if (backendMessage?.includes('expired') || backendMessage?.includes('ExpiredCodeException')) {
          errorMessage = 'Verification code has expired. Please request a new one.'
        } else {
          errorMessage = backendMessage || errorMessage
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setIsLoading(true)
    
    try {
      await publicApi.resendVerificationCode(signUpData.email)
      toast.success('Verification code resent!')
    } catch (error: any) {
      console.error('Resend error:', error)
      toast.error('Failed to resend code')
    } finally {
      setIsLoading(false)
    }
  }

  const goBack = () => {
    if (step === 'verification') {
      setStep('credentials')
    }
  }

  const getStepTitle = () => {
    switch (step) {
      case 'credentials':
        return 'Create your account'
      case 'verification':
        return 'Verify your email'
      default:
        return 'Sign Up'
    }
  }

  const getStepDescription = () => {
    switch (step) {
      case 'credentials':
        return 'Enter your details to get started'
      case 'verification':
        return `We sent a verification code to ${signUpData.email}`
      default:
        return ''
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="spinner h-12 w-12"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 animate-fade-in-up">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>
          <h2 className="text-3xl font-bold text-text mb-2">
            {getStepTitle()}
          </h2>
          <p className="text-text-muted">
            {step === 'credentials' ? (
              <>
                Already have an account?{' '}
                <Link to="/login" className="link font-semibold">
                  Sign in
                </Link>
              </>
            ) : (
              getStepDescription()
            )}
          </p>
          
          {/* Progress indicator */}
          <div className="mt-6 flex justify-center gap-2">
            {['credentials', 'verification'].map((stepName, index) => {
              const currentStepIndex = ['credentials', 'verification'].indexOf(step)
              const stepIndex = index
              
              return (
                <div
                  key={stepName}
                  className={`h-1.5 w-12 rounded-full transition-all duration-300 ${
                    stepName === step
                      ? 'bg-accent w-16'
                      : stepIndex < currentStepIndex
                      ? 'bg-accent-2'
                      : 'bg-border'
                  }`}
                />
              )
            })}
          </div>
        </div>
        
        {/* Credentials Step */}
        {step === 'credentials' && (
          <div className="card p-8">
            <form className="space-y-5" onSubmit={handleCredentialsSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-text mb-2">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    className="input-field"
                    placeholder="John"
                    value={signUpData.firstName}
                    onChange={(e) => updateSignUpData('firstName', e.target.value)}
                  />
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-text mb-2">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    className="input-field"
                    placeholder="Doe"
                    value={signUpData.lastName}
                    onChange={(e) => updateSignUpData('lastName', e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-text mb-2">
                  Company Name
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  className="input-field"
                  placeholder="Acme Corp"
                  value={signUpData.companyName}
                  onChange={(e) => updateSignUpData('companyName', e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-field"
                  placeholder="you@example.com"
                  value={signUpData.email}
                  onChange={(e) => updateSignUpData('email', e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="input-field pr-10"
                    placeholder="Enter your password"
                    value={signUpData.password}
                    onChange={(e) => updateSignUpData('password', e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-text"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                
                {signUpData.password && (
                  <div className="mt-3 space-y-1.5">
                    {Object.entries(validatePassword(signUpData.password)).map(([key, isValid]) => {
                      const labels = {
                        minLength: 'At least 8 characters',
                        hasUppercase: 'One uppercase letter',
                        hasLowercase: 'One lowercase letter',
                        hasNumber: 'One number'
                      }
                      
                      return (
                        <div key={key} className={`text-xs flex items-center gap-2 ${isValid ? 'text-success' : 'text-text-muted'}`}>
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center ${isValid ? 'bg-success/20' : 'bg-border'}`}>
                            {isValid && <CheckCircle className="w-3 h-3" />}
                          </div>
                          {labels[key as keyof typeof labels]}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input-field"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {signUpData.password !== confirmPassword && confirmPassword && (
                  <p className="mt-2 text-sm text-destructive">Passwords do not match</p>
                )}
              </div>
              
              <div className="flex items-start">
                <input
                  id="acceptTerms"
                  name="acceptTerms"
                  type="checkbox"
                  className="h-4 w-4 rounded border-border bg-bg text-accent focus:ring-accent focus:ring-offset-bg mt-1"
                  checked={signUpData.acceptedTerms}
                  onChange={(e) => updateSignUpData('acceptedTerms', e.target.checked)}
                />
                <label htmlFor="acceptTerms" className="ml-3 block text-sm text-text-muted">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" className="link">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" target="_blank" className="link">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <button
                type="submit"
                disabled={
                  isLoading || 
                  signUpData.password !== confirmPassword || 
                  !isPasswordValid(signUpData.password) ||
                  !signUpData.email || 
                  !signUpData.firstName.trim() || 
                  !signUpData.lastName.trim() || 
                  !signUpData.companyName.trim() ||
                  !signUpData.acceptedTerms
                }
                className="w-full px-5 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-accent/20"
              >
                {isLoading ? (
                  <div className="spinner h-5 w-5" />
                ) : (
                  <>
                    <span>Continue</span>
                    <ChevronRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Verification Step */}
        {step === 'verification' && (
          <div className="card p-8">
            <form className="space-y-6" onSubmit={handleVerificationSubmit}>
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-text mb-2">
                  Verification Code
                </label>
                <input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  required
                  maxLength={6}
                  className="input-field text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                />
                <p className="mt-2 text-sm text-text-muted text-center">
                  Enter the 6-digit code sent to your email
                </p>
              </div>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isLoading}
                  className="text-sm link disabled:opacity-50"
                >
                  Didn't receive the code? Resend
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={isLoading}
                  className="flex-1 px-5 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading || verificationCode.length !== 6}
                  className="flex-1 px-5 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-accent/20"
                >
                  {isLoading ? (
                    <div className="spinner h-5 w-5" />
                  ) : (
                    <>
                      <span>Verify & Sign In</span>
                      <ChevronRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
